<script lang="ts" setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useUserVariablesStore } from '../../store/userVariables';
import { useRoute } from 'vue-router';
import FaqView from '../../faq/FaqView.vue';
import { useStyleStore } from '../../store/styleStore';

const styleStore = useStyleStore()
const route = useRoute()
const translation = computed(() => userVariablesStore.currentLanguageData);
const privacyNoticeVisible = ref(false)
const legalNoticeVisible = ref(false)
const defaultLanguage = ref('en');
const userVariablesStore = useUserVariablesStore();
const currentLanguage = ref<string>(route.query.lang as string || defaultLanguage.value);
const brevoLoading = ref(true)
const brevoModalVisible = ref(false)
const faqVisible = ref(false)
const notAiLabInstance = ref(false)

const allowedHostnames = [
  'www.tuev-risk-navigator.ai',
  'localhost',
  'staging.tuev-risk-navigator.ai',
];


const checkInstance = () => {
  const currentHostname = window.location.hostname.trim().toLowerCase(); 

  notAiLabInstance.value = allowedHostnames.includes(currentHostname);
};




const customDisclaimer = computed(() => {
  return currentLanguage.value === 'de' ? styleStore.styles.mptDisclaimerDe : styleStore.styles.mptDisclaimerEn;
});

const customImprint = computed(() => {
  return currentLanguage.value === 'de' ? styleStore.styles.mptImprintDe : styleStore.styles.mptImprintEn;
});

const customPrivacy = computed(() => {
  return currentLanguage.value === 'de' ? styleStore.styles.mptDataPrivacyDe : styleStore.styles.mptDataPrivacyEn;
});



const openBrevoModal = () => {
  brevoModalVisible.value = true;
  brevoLoading.value = true;
}

const initPage = async () => {
  await userVariablesStore.loadLanguageData(currentLanguage.value || defaultLanguage.value);
  userVariablesStore.setLanguage(currentLanguage.value || defaultLanguage.value);
};

const cleanLink = (link: string) => {
  return link.replace(/^https?:\/\//, '');
};

watch(
  () => route.query.lang,
  async (newLang: any) => {
    if (newLang) {
      currentLanguage.value = newLang;
    }
  }
);

const isGeneralLanguage = computed(() => {
  return translation.value.languageIdentifier === 'ailab-general-en' || translation.value.languageIdentifier === 'ailab-general-de';
});

onMounted(async () => {
  await initPage();
  checkInstance();

});

</script>

<template>
    <div v-if="styleStore.isStylesLoaded">
        <div class="row disclaimer justify-content-between" id="footer" v-if="!translationIsLoading && translation">

        <div v-if="isGeneralLanguage" class="disclaimer-text col-8 text-left" v-html="customDisclaimer.value "></div>
        <div v-else class="disclaimer-text col-8 text-left" v-html="translation.staticTexts.disclaimer"></div>
        

        <div class="col-4 text-right menuBottomRight">
          <a v-if="styleStore.styles.customLink1.value" :href="styleStore.styles.customLink1.value" target="_blank" class="modalLink">{{ cleanLink(styleStore.styles.customLink1.value) }}</a><br>
          <a v-if="styleStore.styles.customLink2.value" :href="styleStore.styles.customLink2.value" target="_blank" class="modalLink">{{ cleanLink(styleStore.styles.customLink2.value) }}</a><br  v-if="styleStore.styles.customLink2.value">
          <a class="modalLink"  @click="privacyNoticeVisible = true">{{ translation.staticTexts.privacyNoticeTitle }}</a><br>
          <a class="modalLink"  @click="legalNoticeVisible = true">{{ translation.staticTexts.legalNoticeTitle }}</a><br>
          <a v-if="styleStore.styles.faqVisible.value" class="modalLink" @click="faqVisible = true">{{ translation.staticTexts.faqTitle }}</a><br><br>
          <!-- color must be made dynamically -->
          <a v-if="notAiLabInstance" style="cursor: pointer;color: #00DCE6 !important;" @click="openBrevoModal">Stay updated</a>
          <br><br>

        </div>
      </div>

      <!-- Modals -->
      <Dialog class="idpDialog dataPrivacyDialog" v-if="translation && translation.staticTexts" v-model:visible="privacyNoticeVisible" modal :header="translation.staticTexts.privacyNoticeTitle"  :style="{ width: '40rem' }">
        <div class="mb-2 textModal" v-if="isGeneralLanguage" v-html="customPrivacy.value"></div>
        <div class="mb-2 textModal" v-else v-html="translation.staticTexts.privacyNoticeText"></div>
      </Dialog>

      <Dialog class="idpDialog impressumDialog" v-if="translation && translation.staticTexts" v-model:visible="legalNoticeVisible" modal :header="translation.staticTexts.legalNoticeTitle" :style="{ width: '40rem' }">
        <div class="mb-2 textModal" v-if="isGeneralLanguage" v-html="customImprint.value"></div>
        <div class="mb-2 textModal" v-else v-html="translation.staticTexts.legalNoticeText"></div>
      </Dialog>

      <Dialog v-if="notAiLabInstance" class="idpDialog" v-model:visible="brevoModalVisible" modal header="Stay updated"
        :style="{ width: '40rem', height: '780px', background: '#eff2f7' }">
        <div mb-2>
          <div class="flex" v-if="brevoLoading">
            <ProgressSpinner class="mx-auto my-auto" style="width: 50px; height: 50px" strokeWidth="8"
              fill="transparent" animationDuration=".5s" aria-label="Custom ProgressSpinner" />
          </div>
          <iframe :height="'650px'" @load="brevoLoading = false" :width="'100%'"
            src="https://6d8f25b2.sibforms.com/serve/MUIFAKXUUgYYWIYQYN4jBAaB8-AlMHEGjAajB0YnzJV1_axEQGMoZxR0xzXll3ACMr3zLBuiO3XnnaykzikNxnHXmcY8-75Omi9l0Pudr2qmh4CFeAAGuUx9IWtbEL0vzB8rJFZKgXUWZjM0i4cD4iuCUdOiUlgCW9Hcfs9bqFcNLjmXDoAAq6SiOUDQ0RA0APspO8NcoLYcm1TK"></iframe>
        </div>
      </Dialog>

      <!-- FAQ Drawer -->
            <Drawer v-model:visible="faqVisible" class="!w-full md:!w-96 lg:!w-[90rem] faqDrawer">
              <template #header>
                <div class="faqTitle text-2xl font-bold">
                  {{ translation?.staticTexts?.faqTitle }}
                </div>
              </template>
              <faq-view></faq-view>
            </Drawer>
    </div>
</template>

<style>


.resizable-content {
        height: 100%;
        width: 100%;
        background-color: aqua;
    }
</style>